import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  normalizedPreflightTenantId,
  preflightKeys,
  preflightListKeyParams,
} from "@/lib/queries";
import { getLocalUserFacingApiMessage } from "@/lib/api-client";
import { createReport } from "../api/preflight.api";
import {
  bumpMarketReportCount,
  invalidateMarketAggregates,
} from "../lib/market-cache";
import { usePreflightDraftStore } from "../stores/preflight-draft.store";
import type {
  CreateReportInput,
  CreateReportResult,
  PreflightMarket,
  PreflightMarketWithCount,
  PreflightReportListPage,
  PreflightReportSummary,
} from "../types/preflight.types";

type ListData = InfiniteData<PreflightReportListPage, string | undefined>;

interface PendingRowInput {
  reportId: string;
  input: CreateReportInput;
  pendingRowName: string;
  market: PreflightMarket | null;
}

function buildPendingRow({
  reportId,
  input,
  pendingRowName,
  market,
}: PendingRowInput): PreflightReportSummary {
  const now = new Date().toISOString();
  return {
    id: reportId,
    name: input.name?.trim() || pendingRowName,
    brandName: input.brandName,
    platform: input.platform,
    placement: input.placement,
    status: "QUEUED",
    overallScore: null,
    overallBand: null,
    analyzerVersion: null,
    assetCount: input.uploadIds.length + input.urls.length,
    scoredCount: 0,
    failedCount: 0,
    criteriaPassedCount: 0,
    criteriaFailedCount: 0,
    createdBy: null,
    market,
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * The market the report was created in, read from the cached market list the create
 * form selects from. Null until that list is cached — the row then shows the same
 * placeholder a legacy report does, and reconciles on the first list fetch.
 */
function getCachedMarket(
  queryClient: QueryClient,
  tenantId: string,
  marketId: string | undefined,
): PreflightMarket | null {
  if (!marketId) return null;
  const markets = queryClient.getQueryData<PreflightMarketWithCount[]>(
    preflightKeys.markets(tenantId),
  );
  const market = markets?.find((m) => m.id === marketId);
  return market
    ? { id: market.id, kind: market.kind, name: market.name, code: market.code }
    : null;
}

/** Whether a cached list's market scope covers the new report (all-markets, or its own market). */
function isRowInListScope(key: QueryKey, row: PreflightReportSummary): boolean {
  const scope = preflightListKeyParams(key)?.market;
  return !scope || scope === row.market?.id;
}

/**
 * Prepend the synthetic pending row into the first page of every cached reports
 * list whose market scope covers it, so it appears immediately without surfacing
 * in a differently scoped table. Reconciled by `reportId` on the first successful
 * list/detail fetch (poll → invalidate).
 */
function prependPendingRow(
  queryClient: QueryClient,
  tenantId: string,
  row: PreflightReportSummary,
): void {
  const covering = {
    queryKey: preflightKeys.listsForTenant(tenantId),
    predicate: (query: { queryKey: QueryKey }) =>
      isRowInListScope(query.queryKey, row),
  };
  const entries = queryClient.getQueriesData<ListData>(covering);
  let prepended = false;
  for (const [key, data] of entries) {
    if (!data || data.pages.length === 0) continue;
    const [firstPage, ...rest] = data.pages;
    queryClient.setQueryData<ListData>(key, {
      ...data,
      pages: [
        { ...firstPage!, items: [row, ...firstPage!.items] },
        ...rest,
      ],
    });
    prepended = true;
  }
  if (!prepended) {
    void queryClient.invalidateQueries(covering);
  }
}

/**
 * Create-report mutation (KTD2 generate flow). On success: optimistically prepend a
 * QUEUED row, clear the draft, and toast — the user stays on the Hub and the new row
 * begins polling (U6). On failure: the draft is preserved and an error toast shown.
 * `createReport` returns only `{ reportId }`, so the row is seeded as QUEUED.
 */
export function useCreateReport() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  return useMutation<CreateReportResult, Error, CreateReportInput>({
    mutationFn: (input) => createReport(input),
    onSuccess: (result, input) => {
      const tenantId = normalizedPreflightTenantId(activeTenantId);
      prependPendingRow(
        queryClient,
        tenantId,
        buildPendingRow({
          reportId: result.reportId,
          input,
          pendingRowName: t("preflight.pendingRowName"),
          market: getCachedMarket(queryClient, tenantId, input.marketId),
        }),
      );
      // Hub empty states key off market report counts — bump immediately so the
      // first report exits the no-reports state before the markets refetch lands.
      if (input.marketId) {
        bumpMarketReportCount(queryClient, tenantId, input.marketId, 1);
      }
      invalidateMarketAggregates(queryClient, tenantId);
      usePreflightDraftStore.getState().clear();
      toast.success(t("preflight.toast.analysisStarted"));
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}
